import { useState, useEffect,useRef } from "react";
import { __ } from "@wordpress/i18n";
import apiFetch from "@wordpress/api-fetch";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import ContentHeader from "../../components/ContentHeader";
import TagInput from "../../components/TagInput/TagInput";

const RoleManager = () => {
	const [isSaving, setIsSaving] = useState(false);
	const [isSaved, setIsSaved] = useState(false);
	const [roles, setRoles] = useState({});
	const [users, setUsers] = useState([]);
	const [searchUser, setSearchUser] = useState("");
	const [filteredUsers, setFilteredUsers] = useState([]);
	const [showDropdown, setShowDropdown] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);
	const [currentPrimaryRole, setCurrentPrimaryRole] = useState("");
	const [selectedRoles, setSelectedRoles] = useState([]);
	const [newPrimaryRole, setNewPrimaryRole] = useState("");
	
	const searchRef = useRef(null);

	useEffect(() => {
		loadRoles();
		loadUsers();
		
		// Close dropdown when clicking outside
		const handleClickOutside = (event) => {
			if (searchRef.current && !searchRef.current.contains(event.target)) {
				setShowDropdown(false);
			}
		};
		
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	useEffect(() => {
		// Filter users based on search input
		if (searchUser) {
			const filtered = users.filter(user => 
				user.display_name.toLowerCase().includes(searchUser.toLowerCase())
			);
			setFilteredUsers(filtered);
		} else {
			setFilteredUsers([]);
		}
	}, [searchUser, users]);

	// Load user role when a user is selected
	useEffect(() => {
		if (selectedUser) {
			loadCurrentPrimaryRole();
		}
	}, [selectedUser]);

	// Reset selected roles if they include the new primary role
	useEffect(() => {
		if (newPrimaryRole) {
			setSelectedRoles(prevRoles => prevRoles.filter(role => role !== newPrimaryRole));
		}
	}, [newPrimaryRole]);

	const loadRoles = async () => {
		try {
			const response = await apiFetch({ path: "/plac/v1/all-roles" });
			setRoles(response.roles);
			// Set default primary role
			if (response.roles && Object.keys(response.roles).length > 0) {
				setNewPrimaryRole(Object.keys(response.roles)[0]);
			}
		} catch (error) {
			console.error("Error loading roles:", error);
		}
	};
	
	const loadUsers = async () => {
		try {
			const response = await apiFetch({ path: "/plac/v1/all-users" });
			setUsers(response);
		} catch (error) {
			console.error("Error loading users:", error);
		}
	};

	const loadCurrentPrimaryRole = async () => {
		if (!selectedUser) return;
		
		try {
			const response = await apiFetch({
				path: "/plac/v1/get-current-primary-role",
				method: "POST",
				data: {
					_wpnonce: window.placAdminData.nonce,
					user: selectedUser.display_name,
				},
			});
	
			setCurrentPrimaryRole(response.role);
			
			// Ensure the current role isn't selected in the new primary role dropdown
			if (response.role && response.role === newPrimaryRole) {
				// Find the first role that isn't the current one
				const availableRoles = Object.keys(roles).filter(role => role !== response.role);
				if (availableRoles.length > 0) {
					setNewPrimaryRole(availableRoles[0]);
				}
			}
		} catch (error) {
			console.error("Error loading role:", error);
			setCurrentPrimaryRole("");
		}
	};
	
	const handleSearchChange = (e) => {
		setSearchUser(e.target.value);
		setShowDropdown(true);
		setSelectedUser(null);
		setCurrentPrimaryRole("");
		setSelectedRoles([]);
	};
	
	const selectUser = (user) => {
		setSelectedUser(user);
		setSearchUser(user.display_name);
		setShowDropdown(false);
		setSelectedRoles([]);
	};
	
	const handleNewRoleChange = (e) => {
		const selected = e.target.value;
		setNewPrimaryRole(selected);
		
		// Remove the selected primary role from other roles if present
		setSelectedRoles(prevRoles => prevRoles.filter(role => role !== selected));
	};
	
	const handleTagChange = (tags) => {
		// Filter out the primary roles
		const filteredTags = Array.isArray(tags) 
			? tags.filter(role => role !== newPrimaryRole && role !== currentPrimaryRole)
			: [];
		
		setSelectedRoles(filteredTags);
	};
	
	const handleSaveChanges = async (e) => {
		e.preventDefault();
		
		if (!selectedUser) {
			alert("Please select a user first");
			return;
		}		
		setIsSaving(true);
		try {
			const response = await apiFetch({
				path: "/plac/v1/migrate-role",
				method: "POST",
				data: {
					_wpnonce: window.placAdminData.nonce,
					user: selectedUser.display_name,
					new_primary_role: newPrimaryRole,
					other_roles: selectedRoles,
				},
			});
			console.log(response);
			setIsSaved(true);
			
			// Reset state after successful save
			setTimeout(() => {
				setIsSaved(false);
			}, 3000);
		} catch (error) {
			console.error("Error saving role:", error);
		} finally {
			setIsSaving(false);
		}
	};

	// Filter roles for the dropdown (exclude current primary role)
	const availablePrimaryRoles = Object.entries(roles)
		.filter(([role]) => role !== currentPrimaryRole)
		.reduce((obj, [key, value]) => {
			obj[key] = value;
			return obj;
		}, {});

	// Filter roles for TagInput (exclude both current and new primary role)
	const availableOtherRoles = Object.entries(roles)
		.filter(([role]) => role !== currentPrimaryRole && role !== newPrimaryRole)
		.map(([value, label]) => ({ value, label }));

	return (
		<>
			<div className="settings-container">
				<ContentHeader
					title={"Change user's roles"}
					description={
						"Easily add or remove roles for any user. You can assign multiple roles to a single user. Simply enter the username in the search field to get started."
					}
				/>

				<div className="content-section">
					<div className="leftSection">
						<h2 className="title header-1 primary-text-color semi-bold">
							{__("Lookup user", "access-control")}
						</h2>
						<p className="description header-2 primary-text-color regular">
							{__(
								"Lorem ipsum dolor sit amet consectetur. Ut diam nulla leo viverra dictumst posuere fames.",
								"access-control",
							)}
						</p>
					</div>

					<div className="rightSection">
						<div className="setting-item">
							<div className="input-container">
								<OverlayTrigger
									placement="top"
									overlay={
										<Tooltip id="lookup-user-tooltip">
											{__(
												"Search for a user",
												"access-control",
											)}
										</Tooltip>
									}
								>
									<img
										src={`${window.placAdminData.pluginUrl}/assets/images/question.svg`}
										alt="icon"
										className="icon"
										style={{ cursor: "pointer" }}
									/>
								</OverlayTrigger>

								<div className="input-field" ref={searchRef} style={{ position: "relative" }}>
									<input
										type="text"
										className="reset-input"
										placeholder="Search"
										value={searchUser}
										onChange={handleSearchChange}
										onClick={() => setShowDropdown(true)}
									/>
									
									{showDropdown && filteredUsers.length > 0 && (
										<div style={{
											position: "absolute",
											top: "100%",
											left: 0,
											right: 0,
											zIndex: 10,
											background: "white",
											border: "1px solid #ddd",
											borderRadius: "4px",
											maxHeight: "200px",
											maxWidth: "374px",
											overflowY: "auto"
										}}>
											{filteredUsers.map((user, index) => (
												<div 
													key={index}
													onClick={() => selectUser(user)}
													style={{
														padding: "8px 12px",
														cursor: "pointer",
														borderBottom: index < filteredUsers.length - 1 ? "1px solid #eee" : "none",
														hover: { background: "#f5f5f5" }
													}}
													onMouseOver={(e) => e.currentTarget.style.background = "#f5f5f5"}
													onMouseOut={(e) => e.currentTarget.style.background = "white"}
												>
													{user.display_name}
												</div>
											))}
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="content-section">
					<div className="leftSection">
						<h2 className="title header-1 primary-text-color semi-bold">
							{__("Current primary role", "access-control")}
						</h2>
					</div>

					<div className="rightSection">
						<div className="setting-item">
							<div className="input-container">
								<OverlayTrigger
									placement="top"
									overlay={
										<Tooltip id="current-role-tooltip">
											{__(
												"Current primary role",
												"access-control",
											)}
										</Tooltip>
									}
								>
									<img
										src={`${window.placAdminData.pluginUrl}/assets/images/question.svg`}
										alt="icon"
										className="icon"
										style={{ cursor: "pointer" }}
									/>
								</OverlayTrigger>

								<div className="input-field">
									<input
										type="text"
										className="reset-input"
										disabled
										value={currentPrimaryRole ? roles[currentPrimaryRole] || currentPrimaryRole : "No role assigned"}
										readOnly
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="content-section">
					<div className="leftSection">
						<h2 className="title header-1 primary-text-color semi-bold">
							{__("New primary role", "access-control")}
						</h2>
					</div>

					<div className="rightSection">
						<div className="setting-item">
							<div className="input-container">
								<OverlayTrigger
									placement="top"
									overlay={
										<Tooltip id="new-role-tooltip">
											{__(
												"New primary role",
												"access-control",
											)}
										</Tooltip>
									}
								>
									<img
										src={`${window.placAdminData.pluginUrl}/assets/images/question.svg`}
										alt="icon"
										className="icon"
										style={{ cursor: "pointer" }}
									/>
								</OverlayTrigger>

								<div className="input-field">
									<select 
										name="new-role"
										className="reset-input"
										value={newPrimaryRole}
										onChange={handleNewRoleChange}
										disabled={!selectedUser}
									>
										{Object.keys(availablePrimaryRoles).map((role) => (
											<option key={role} value={role}>{roles[role]}</option>
										))}
									</select>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="content-section">
					<div className="leftSection">
						<h2 className="title header-1 primary-text-color semi-bold">
							{__("Assign other user role(s)", "access-control")}
						</h2>
					</div>

					<div className="rightSection">
						<div className="setting-item">
							<div className="input-container">
								<OverlayTrigger
									placement="top"
									overlay={
										<Tooltip id="new-role-tooltip">
											{__(
												"Assign other user role",
												"access-control",
											)}
										</Tooltip>
									}
								>
									<img
										src={`${window.placAdminData.pluginUrl}/assets/images/question.svg`}
										alt="icon"
										className="icon"
										style={{ cursor: "pointer" }}
									/>
								</OverlayTrigger>

								<div className="input-field" style={{width:"unset"}}>
									<TagInput 
										name='other-roles' 
										type='role' 
										handleChange={handleTagChange}
										whitelist={availableOtherRoles}
										disabled={!selectedUser}
									/>
								</div>
							</div>
							<div className="global-reset">
								{isSaving ? (
									<div
										className="saving-changes"
										style={{
											paddingLeft: "20px",
											color: "#873eff",
										}}
									>
										<div className="pl-access-control-loading-spinner"></div>
										{__("Changes Saving...", "access-control")}
									</div>
								) : isSaved ? (
									<div
										className="success-checkmark"
										style={{
											paddingLeft: "20px",
											color: "#873eff",
										}}
									>
										<img
											src={`${window.placAdminData.pluginUrl}/assets/images/success-tick-active.svg`}
											alt="quotation-icon"
										/>
										{__("Changes Saved", "access-control")}
									</div>
								) : (
									<a
										href="#"
										className={`reset-txt-btn header-1 semi-bold purple-text-color ${!selectedUser ? 'disabled' : ''}`}
										onClick={handleSaveChanges}
										style={{ opacity: !selectedUser ? 0.5 : 1 }}
									>
										{__(
											"Migrate user role",
											"access-control",
										)}
									</a>
								)}
							</div>
						</div>
					</div>
				</div>

				<div className="footer-section">
					<button 
						className={`save-button disabled purple-bg-40 secondary-text-color`} >
						{__("Save Change", "access-control")}
					</button>
					<button className={`discard-button disabled purple-text-color btn-border`} >
						{__("Reset to Default", "access-control")}
					</button>
				</div>
			</div>
		</>
	);
};

export default RoleManager;
